import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardActionArea, 
  Chip,
  Divider,
  Grid,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Alert
} from '@mui/material';
import { format } from 'date-fns';
import ja from 'date-fns/locale/ja';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import ClearIcon from '@mui/icons-material/Clear';

// 会話カードコンポーネント
const ConversationCard = ({ conversation, onClick, onToggleFavorite }) => {
  return (
    <Card className="conversation-card" sx={{ mb: 2 }} elevation={2}>
      <CardActionArea onClick={() => onClick(conversation)}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" component="div">
              {conversation.title}
            </Typography>
            <IconButton 
              onClick={(e) => {
                e.stopPropagation(); // カード全体のクリックイベントを防止
                onToggleFavorite(conversation.id);
              }}
              color="warning"
              size="small"
              aria-label="お気に入りから削除"
              sx={{ p: 0.5 }}
            >
              <StarIcon />
            </IconButton>
          </Box>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {conversation.timestamp && !isNaN(new Date(conversation.timestamp).getTime()) 
              ? format(new Date(conversation.timestamp), 'yyyy年MM月dd日 HH:mm', { locale: ja })
              : conversation.startTime && !isNaN(new Date(conversation.startTime).getTime())
                ? format(new Date(conversation.startTime), 'yyyy年MM月dd日 HH:mm', { locale: ja })
                : '日付なし'
            }
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {conversation.summary || conversation.content ? 
              (conversation.summary || conversation.content.substring(0, 100) + (conversation.content.length > 100 ? '...' : '')) 
              : '内容なし'
            }
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {conversation.tags && Array.isArray(conversation.tags) && conversation.tags.length > 0 && (
              conversation.tags.map((tag, index) => (
                <Chip 
                  key={index} 
                  label={tag} 
                  size="small" 
                  className="tag-chip"
                />
              ))
            )}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const FavoritesView = ({ conversations, onConversationClick, searchQuery, favorites, onToggleFavorite, onSearch }) => {
  // お気に入りに登録された会話を取得
  const favoriteConversations = conversations.filter(conv => favorites.includes(conv.id));
  
  // ローカル検索クエリーの状態 - 外部の検索クエリーは無視する
  const [localSearchQuery, setLocalSearchQuery] = React.useState('');
  
  // 検索クエリによるフィルタリング
  const filteredConversations = localSearchQuery
    ? favoriteConversations.filter(conversation => {
        const query = localSearchQuery.toLowerCase();
        return (
          (conversation.title && conversation.title.toLowerCase().includes(query)) ||
          (conversation.content && conversation.content.toLowerCase().includes(query)) ||
          (conversation.summary && conversation.summary.toLowerCase().includes(query))
        );
      })
    : favoriteConversations;
    
  // 検索処理 - ローカル検索のみ更新する
  const handleSearch = (event) => {
    const newQuery = event.target.value;
    setLocalSearchQuery(newQuery);
    // グローバルの検索状態には影響を与えない
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>お気に入り</Typography>
      <Divider sx={{ mb: 3 }} />
      
      {/* 検索ボックス */}
      <TextField
        fullWidth
        variant="outlined"
        placeholder="お気に入りを検索"
        value={localSearchQuery}
        onChange={handleSearch}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: localSearchQuery ? (
            <InputAdornment position="end">
              <IconButton
                onClick={() => {
                  setLocalSearchQuery('');
                  if (onSearch) onSearch('');
                }}
                edge="end"
                size="small"
              >
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      
      {/* 会話リスト */}
      <Paper elevation={2} sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            お気に入り会話
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredConversations.length}件の会話
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        {filteredConversations.length === 0 ? (
          <Box sx={{ py: 4 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              お気に入りに登録された会話がありません
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              会話詳細画面で星マークをクリックするとお気に入りに登録できます
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filteredConversations.map(conversation => (
              <Grid item xs={12} sm={6} md={4} key={conversation.id}>
                <ConversationCard 
                  conversation={conversation} 
                  onClick={onConversationClick}
                  onToggleFavorite={onToggleFavorite}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default FavoritesView;
